// Utils
import createDeepEqualSelector from '../../utils/reselect/createDeepEqualSelector';

const selectSelectedTableCells = (state) => state.selectedTableCells;
const selectRowId = (state, props) => props.rowId || props.rowIndex;
const selectCellName = (state, props) => props.cellName || props.columnIndex;

const selectHoveredTableCellsColumnId = createDeepEqualSelector(
    selectSelectedTableCells,
    (selectedTableCellsState) => selectedTableCellsState.hoveredColumnId
);

const selectHoveredTableCellRowId = createDeepEqualSelector(
    selectSelectedTableCells,
    (selectedTableCellsState) => selectedTableCellsState.hoveredRowId
);

const makeSelectIsRowHovered = () =>
    createDeepEqualSelector(
        [selectHoveredTableCellRowId, selectRowId],
        (hoveredTableCellRowIdState, rowIdState) =>
            hoveredTableCellRowIdState === rowIdState
    );

const makeSelectIsCellHovered = () =>
    createDeepEqualSelector(
        [
            selectHoveredTableCellsColumnId,
            selectHoveredTableCellRowId,
            selectRowId,
            selectCellName
        ],
        (
            hoveredTableCellsColumnIdState,
            hoveredTableCellRowIdState,
            rowIdState,
            cellNameState
        ) =>
            hoveredTableCellRowIdState === rowIdState &&
            hoveredTableCellsColumnIdState === cellNameState
    );

const selectActiveTableCellsColumnId = createDeepEqualSelector(
    selectSelectedTableCells,
    (selectedTableCellsState) => selectedTableCellsState.activeColumnId
);

const selectActiveTableCellRowIds = createDeepEqualSelector(
    selectSelectedTableCells,
    (selectedTableCellsState) => selectedTableCellsState.activeRowIds
);

const selectActiveTableRowsCount = createDeepEqualSelector(
    selectActiveTableCellRowIds,
    (activeTableCellRowIds) => activeTableCellRowIds.length
);

const selectActiveTableRowsExist = createDeepEqualSelector(
    selectActiveTableCellRowIds,
    (activeTableCellRowIds) => !!activeTableCellRowIds.length
);

const selectMultipleActiveTableRowsExist = createDeepEqualSelector(
    selectActiveTableCellRowIds,
    (activeTableCellRowIds) => activeTableCellRowIds.length >= 2
);

const selectActiveTableCellRowsHashMap = createDeepEqualSelector(
    selectActiveTableCellRowIds,
    (selectedTableCellRowsState) => {
        if (!selectedTableCellRowsState.length) return {};
        return selectedTableCellRowsState.reduce(
            (accumulator, currentValue) => {
                accumulator[currentValue] = true;
                return accumulator;
            },
            {}
        );
    }
);

const makeSelectIsRowActive = () =>
    createDeepEqualSelector(
        [selectActiveTableCellRowsHashMap, selectRowId],
        (selectedTableCellRowsHashMapState, rowIdState) => {
            if (Array.isArray(rowIdState)) {
                return rowIdState.length === 0
                    ? false
                    : rowIdState.reduce((accumulator, currentValue) => {
                          if (!accumulator) return false;
                          return !!selectedTableCellRowsHashMapState[
                              currentValue
                          ];
                      }, true);
            }
            return !!selectedTableCellRowsHashMapState[rowIdState];
        }
    );

const makeSelectAreSomeRowsActive = () =>
    createDeepEqualSelector(
        [selectActiveTableCellRowsHashMap, selectRowId],
        (selectedTableCellRowsHashMapState, rowIdState) => {
            if (Array.isArray(rowIdState)) {
                return rowIdState.reduce((accumulator, currentValue) => {
                    if (accumulator) return true;
                    return !!selectedTableCellRowsHashMapState[currentValue];
                }, false);
            }
            return !!selectedTableCellRowsHashMapState[rowIdState];
        }
    );

const selectSelectedTableCellsColumnName = createDeepEqualSelector(
    selectSelectedTableCells,
    (selectedTableCellsState) => selectedTableCellsState.selectedColumnId
);

const selectSelectedTableCellRows = createDeepEqualSelector(
    selectSelectedTableCells,
    (selectedTableCellsState) => selectedTableCellsState.selectedRowIds
);

const selectSelectedTableRowsExist = createDeepEqualSelector(
    selectSelectedTableCellRows,
    (selectedTableCellRowsState) => !!selectedTableCellRowsState.length
);

const selectSelectedTableRowsCount = createDeepEqualSelector(
    selectSelectedTableCellRows,
    (selectedTableCellRowsState) => selectedTableCellRowsState.length
);

const selectLastSelectedTableRow = createDeepEqualSelector(
    selectSelectedTableCellRows,
    (selectedTableCellRowsState) => {
        if (!selectedTableCellRowsState.length) return undefined;
        return selectedTableCellRowsState.slice(-1)[0];
    }
);

const selectIsLastSelectedTableRow = createDeepEqualSelector(
    [selectLastSelectedTableRow, selectRowId],
    (lastSelectedTableRowState, rowIdState) =>
        lastSelectedTableRowState === rowIdState
);

const selectSelectedTableCellRowsHashMap = createDeepEqualSelector(
    selectSelectedTableCellRows,
    (selectedTableCellRowsState) => {
        if (!selectedTableCellRowsState.length) return {};
        return selectedTableCellRowsState.reduce(
            (accumulator, currentValue) => {
                accumulator[currentValue] = true;
                return accumulator;
            },
            {}
        );
    }
);

const makeSelectIsColumnSelected = () =>
    createDeepEqualSelector(
        [selectSelectedTableCellsColumnName, selectCellName],
        (selectedTableCellsColumnNameState, cellNameState) =>
            selectedTableCellsColumnNameState === cellNameState
    );

const makeSelectIsRowSelected = () =>
    createDeepEqualSelector(
        [selectSelectedTableCellRowsHashMap, selectRowId],
        (selectedTableCellRowsHashMapState, rowIdState) => {
            if (Array.isArray(rowIdState)) {
                if (!rowIdState.length) {
                    return false;
                }
                return rowIdState.reduce((accumulator, currentValue) => {
                    if (!accumulator) return false;
                    return !!selectedTableCellRowsHashMapState[currentValue];
                }, true);
            }
            return !!selectedTableCellRowsHashMapState[rowIdState];
        }
    );

const makeSelectAreSomeRowsSelected = () =>
    createDeepEqualSelector(
        [selectSelectedTableCellRowsHashMap, selectRowId],
        (selectedTableCellRowsHashMapState, rowIdState) => {
            if (Array.isArray(rowIdState)) {
                return rowIdState.reduce((accumulator, currentValue) => {
                    if (accumulator) return true;
                    return !!selectedTableCellRowsHashMapState[currentValue];
                }, false);
            }
            return !!selectedTableCellRowsHashMapState[rowIdState];
        }
    );

const makeSelectIsCellSelected = () =>
    createDeepEqualSelector(
        [makeSelectIsColumnSelected(), makeSelectIsRowSelected()],
        (isColumnSelectedState, isRowSelectedState) =>
            !!(isColumnSelectedState && isRowSelectedState)
    );

const makeSelectIsCellActive = () =>
    createDeepEqualSelector(
        [makeSelectIsColumnSelected(), selectIsLastSelectedTableRow],
        (isColumnSelectedState, isLastSelectedTableRowState) => {
            return !!(isColumnSelectedState && isLastSelectedTableRowState);
        }
    );

export {
    selectHoveredTableCellsColumnId,
    selectHoveredTableCellRowId,
    makeSelectIsRowHovered,
    makeSelectIsCellHovered,
    selectActiveTableCellsColumnId,
    selectActiveTableCellRowIds,
    selectActiveTableRowsCount,
    selectActiveTableRowsExist,
    selectMultipleActiveTableRowsExist,
    makeSelectIsRowActive,
    makeSelectAreSomeRowsActive,
    selectSelectedTableCells,
    selectSelectedTableCellsColumnName,
    selectSelectedTableCellRows,
    selectSelectedTableRowsExist,
    selectSelectedTableRowsCount,
    selectLastSelectedTableRow,
    selectIsLastSelectedTableRow,
    selectSelectedTableCellRowsHashMap,
    makeSelectIsColumnSelected,
    makeSelectIsRowSelected,
    makeSelectAreSomeRowsSelected,
    makeSelectIsCellSelected,
    makeSelectIsCellActive
};
